<!-- src/lib/components/Toaster.svelte -->
<!--
  Bottom-right toast stack. Subscribes to the global toastStore and
  renders each live toast. Click-to-dismiss, click-to-action, auto-
  dismiss is handled by the store itself.

  Mounted exactly once from App.svelte so it's always visible.
-->
<script lang="ts">
  import { toastStore, toast } from '../stores/toast.svelte.ts';
  import { fly } from 'svelte/transition';
</script>

<div class="toaster" aria-live="polite" aria-atomic="false">
  {#each toastStore.toasts as t (t.id)}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="toast toast-{t.kind}"
      transition:fly={{ x: 40, duration: 200, opacity: 0 }}
      onclick={() => {
        if (t.onClick) { t.onClick(); toast.dismiss(t.id); }
      }}
    >
      <span class="toast-glyph">
        {#if t.kind === 'success'}✓
        {:else if t.kind === 'error'}✕
        {:else if t.kind === 'warning'}!
        {:else}•{/if}
      </span>
      <div class="toast-body">
        <div class="toast-title">{t.title}</div>
        {#if t.body}<div class="toast-subtitle">{t.body}</div>{/if}
      </div>
      <button
        class="toast-close"
        aria-label="Dismiss"
        onclick={(e) => { e.stopPropagation(); toast.dismiss(t.id); }}
      >×</button>
    </div>
  {/each}
</div>

<style>
  .toaster {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 9000;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    pointer-events: none;
    max-width: 360px;
  }
  .toast {
    pointer-events: auto;
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.75rem 0.85rem 0.75rem 0.75rem;
    border-radius: 6px;
    background: oklch(0.11 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
    font-size: 0.8rem;
    color: oklch(0.92 0.06 310);
    cursor: pointer;
    transition: background 120ms ease;
  }
  .toast:hover {
    background: oklch(0.15 0.025 275);
  }

  /* Kind-specific left-border accent */
  .toast-success { border-left: 3px solid oklch(0.78 0.16 150); }
  .toast-error   { border-left: 3px solid oklch(0.68 0.24 25);  }
  .toast-warning { border-left: 3px solid oklch(0.78 0.16 85);  }
  .toast-info    { border-left: 3px solid oklch(0.68 0.18 265); }

  .toast-glyph {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 0.9rem;
    line-height: 1;
  }
  .toast-success .toast-glyph { color: oklch(0.78 0.16 150); }
  .toast-error   .toast-glyph { color: oklch(0.68 0.24 25);  }
  .toast-warning .toast-glyph { color: oklch(0.78 0.16 85);  }
  .toast-info    .toast-glyph { color: oklch(0.68 0.18 265); }

  .toast-body {
    flex: 1;
    min-width: 0;
  }
  .toast-title {
    font-weight: 600;
    color: oklch(0.96 0.06 310);
    letter-spacing: -0.005em;
  }
  .toast-subtitle {
    font-size: 0.72rem;
    color: oklch(0.68 0.05 310 / 0.75);
    margin-top: 0.15rem;
    line-height: 1.45;
    word-break: break-word;
  }

  .toast-close {
    background: transparent;
    border: none;
    color: oklch(0.60 0.05 310 / 0.50);
    font-size: 1.1rem;
    line-height: 1;
    padding: 0 0.25rem;
    cursor: pointer;
    flex-shrink: 0;
    transition: color 100ms ease;
  }
  .toast-close:hover {
    color: oklch(0.92 0.06 310);
  }
</style>
